import React, { useEffect, useState } from "react";
import { usePremisesStore } from "../../../store/store";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { proxyUrl, handleApiError } from "../../../common/utils";
import axiosInstance from "../../../common/axiosInstance";
import { PageLoader } from "../../../components/loader/Loader";
import {
  useChecklistCategories,
  ChecklistCategory,
} from "./HazMatChecklistCategory/useChecklistCategories";
import { v4 as uuidv4 } from "uuid";

// export interface ChecklistCategory {
//   _id: string;
//   categoryId: string;
//   categoryName: string;
//   createdAt?: string;
// }

interface ChecklistItem {
  id?: string;
  question: string;
  categoryId: string;
}

interface Checklist {
  _id: string;
  name: string;
  premisesId: string;
  materialType?: string;
  items: ChecklistItem[];
  createdAt: string;
  updatedAt: string;
}

interface ChecklistItemRowProps {
  item: ChecklistItem;
  idx: number;
  onChange: (idx: number, field: keyof ChecklistItem, value: any) => void;
  onRemove: (idx: number) => void;
  isViewMode?: boolean;
  categories: ChecklistCategory[];
  categoriesLoading: boolean;
}

const ChecklistItemRow: React.FC<ChecklistItemRowProps> = ({
  item,
  idx,
  onChange,
  onRemove,
  isViewMode = false,
  categories,
  categoriesLoading,
}) => {
  const getCategoryName = (categoryId: string) => {
    const category = categories.find((cat) => cat.categoryId === categoryId);
    return category ? category.categoryName : categoryId;
  };

  return (
    <div className="grid grid-cols-12 gap-4 px-4 py-3 items-start border-t bg-gray-50 mb-2 rounded-md">
      <div className="col-span-4 flex flex-col justify-center">
        {isViewMode ? (
          <input
            type="text"
            className="border rounded px-2 py-2 w-full bg-gray-100"
            value={getCategoryName(item.categoryId)}
            disabled
          />
        ) : (
          <select
            className="border rounded px-2 pr-8 py-2 w-full focus:outline-none focus:ring-0 appearance-none bg-white"
            value={item.categoryId}
            onChange={(e) => onChange(idx, "categoryId", e.target.value)}
            required
            disabled={categoriesLoading}
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='16' height='16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 6l4 4 4-4' stroke='%2361748e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 0.75rem center",
              backgroundSize: "1.25em 1.25em",
            }}
          >
            <option value="">
              {categoriesLoading ? "Loading categories..." : "Select Category"}
            </option>
            {categories.map((category) => (
              <option key={category._id} value={category.categoryId}>
                {category.categoryName}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="col-span-7 flex flex-col justify-center">
        <input
          type="text"
          className="border rounded px-2 py-2 w-full focus:outline-none focus:ring-0"
          placeholder="Enter question"
          value={item.question || ""}
          onChange={(e) => onChange(idx, "question", e.target.value)}
          required
          disabled={isViewMode}
        />
      </div>
      {!isViewMode && (
        <div className="col-span-1 flex items-center justify-end h-full">
          <button
            type="button"
            className="text-red-500 hover:underline font-semibold text-sm px-2 py-1 rounded transition-colors duration-150 focus:outline-none focus:ring-0 border-0 shadow-none"
            onClick={() => onRemove(idx)}
            aria-label="Delete item"
            style={{ boxShadow: "none", border: "none" }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

const ChecklistConfigForm: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: urlId } = useParams();

  const mode = location.pathname.includes("/view/")
    ? "view"
    : location.pathname.includes("/edit/")
    ? "edit"
    : "create";

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
    refreshCategories,
    clearError: clearCategoriesError,
  } = useChecklistCategories({
    autoFetch: true,
    pollingInterval: 30000,
    refreshOnFocus: true,
  });

  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([
    {
      question: "",
      categoryId: "",
      id: uuidv4(),
    },
  ]);
  const [selectedPremises, setSelectedPremises] = useState("");
  const [selectedMaterialType, setSelectedMaterialType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [name, setName] = useState("");
  const { allPremises, fetchPremises } = usePremisesStore();

  useEffect(() => {
    if (allPremises.length === 0) fetchPremises();
  }, []);

  useEffect(() => {
    if (urlId && (isViewMode || isEditMode)) {
      setLoading(true);
      axiosInstance.get(`/api/hazmat-checklist-config/${urlId}`)
        .then((response) => {
          if (response.status === 200) {
            const data = response.data.data;
            setChecklistItems(data.items || []);
            setSelectedPremises(data.premisesId);
            setSelectedMaterialType(data.materialType || "");
            setName(data.name);
          }
        })
        .catch((error) => {
          setError("Failed to load checklist data");
          console.error("Error loading checklist:", error);
          if (error.response?.status === 404) {
            navigate("/hazardous-materials/checklists");
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [urlId, mode, allPremises.length, fetchPremises, navigate]);

  useEffect(() => {
    if (!categoriesError) {
      clearCategoriesError();
    }
  }, [categoriesError, clearCategoriesError]);

  const handleAddItem = () =>
    setChecklistItems((prev) => [
      ...prev,
      {
        question: "",
        categoryId: "",
        id: uuidv4(),
      },
    ]);

  const handleRemoveItem = (idx: number) =>
    setChecklistItems((prev) => prev.filter((_, i) => i !== idx));

  const handleItemChange: ChecklistItemRowProps["onChange"] = (
    idx,
    field,
    value
  ) => {
    setChecklistItems((prev) => {
      const updated = [...prev];
      updated[idx][field] = value;
      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isViewMode) return;

    setError("");
    setSuccess("");
    if (!selectedPremises) return setError("Please select premises.");
    if (checklistItems.length === 0)
      return setError("Add at least one checklist item.");

    const invalidItems = checklistItems.filter((item) => !item.categoryId);
    if (invalidItems.length > 0) {
      return setError("Please select a category for all checklist items.");
    }

    setLoading(true);
    const body = {
      name,
      premisesId: selectedPremises,
      materialType: selectedMaterialType || undefined,
      items: checklistItems.map(({ question, categoryId, id }) => ({
        question,
        categoryId,
        id: id || uuidv4(),
      })),
    };

    try {
      const url = isEditMode ? `/api/hazmat-checklist-config/${urlId}` : `/api/hazmat-checklist-config`;

      const response = await axiosInstance({
        method: isEditMode ? "put" : "post",
        url,
        data: body,
      });

      if (response.status === 200 || response.status === 201) {
        setSuccess("Checklist saved successfully!");
        if (!isEditMode) {
          setChecklistItems([]);
          setSelectedPremises("");
          setSelectedMaterialType("");
          setName("");
        }
        setTimeout(() => {
          navigate("/hazardous-materials/checklists");
        }, 1500);
      } else {
        setError(response.data.error || "Failed to save checklist.");
      }
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to save checklist.");
      console.error("Error saving checklist:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshCategories = () => {
    refreshCategories();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <PageLoader />
      </div>
    );
  }

  return (
    <>
      <div className="px-2 py-4 mt-14 ml-2 min-h-100vh">
        <div className="flex justify-between items-center mb-3">
          <h2 className="mb-3">
            {isViewMode
              ? "View Checklist"
              : isEditMode
              ? "Edit Checklist"
              : "Create Checklist"}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-[#61748e] text-white"
              onClick={() => navigate("/hazardous-materials/checklists")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> BACK
            </button>
          </div>
        </div>
        <form
          className="bg-white rounded-t-lg shadow-md mt-2 p-8 w-full"
          id="checklist-form"
          onSubmit={handleSubmit}
        >
          {error && <div className="text-red-500 mb-2">{error}</div>}
          {success && <div className="text-green-600 mb-2">{success}</div>}
          {categoriesError && (
            <div className="text-orange-500 mb-2">
              Warning: Failed to load categories. Please refresh or contact
              support.
            </div>
          )}

          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">
                Checklist Name
              </label>
              <input
                type="text"
                name="name"
                className="border rounded px-3 py-2 w-full"
                placeholder="Enter checklist name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isViewMode}
                required
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Premises</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={selectedPremises}
                onChange={(e) => setSelectedPremises(e.target.value)}
                disabled={isViewMode}
                required
              >
                <option value="">Select Premises</option>
                {allPremises.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.premisesName}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">
                Material Type (Optional)
              </label>
              <input
                type="text"
                className="border rounded px-3 py-2 w-full"
                placeholder="Enter material type"
                value={selectedMaterialType}
                onChange={(e) => setSelectedMaterialType(e.target.value)}
                disabled={isViewMode}
              />
            </div>
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium">
                Checklist Items
              </label>
              {!isViewMode && (
                <button
                  type="button"
                  onClick={handleRefreshCategories}
                  className="text-sm text-[#0fa9a6] hover:underline flex items-center gap-1"
                  disabled={categoriesLoading}
                >
                  {categoriesLoading ? "Refreshing..." : "Refresh Categories"}
                </button>
              )}
            </div>
            <div className="rounded">
              <div className="grid grid-cols-12 gap-4 bg-gray-100 px-4 py-2 font-semibold text-sm">
                <div className="col-span-4">
                  Category{" "}
                  {categoriesLoading && (
                    <span className="text-xs">(Loading...)</span>
                  )}
                </div>
                <div className="col-span-7">Question</div>
                {!isViewMode && <div className="col-span-1"></div>}
              </div>
              {checklistItems.map((item, idx) => (
                <ChecklistItemRow
                  key={idx}
                  item={item}
                  idx={idx}
                  onChange={handleItemChange}
                  onRemove={handleRemoveItem}
                  isViewMode={isViewMode}
                  categories={categories}
                  categoriesLoading={categoriesLoading}
                />
              ))}
            </div>
            {!isViewMode && (
              <button
                type="button"
                className="mt-3 text-[#00A8A5] bg-[#EAFBFA] px-4 py-1 rounded font-semibold border border-[#00A8A5] transition-colors duration-150 hover:bg-[#00A8A5] hover:text-white focus:outline-none focus:ring-0"
                onClick={handleAddItem}
              >
                Add More
              </button>
            )}
          </div>
        </form>
        <div className="flex rounded-md justify-end gap-4 p-4 bg-[#5A7192]  sticky bottom-0">
          {!isViewMode && (
            <button
              type="submit"
              form="checklist-form"
              className="px-6 py-2 rounded-full bg-[#00A8A8] text-white hover:bg-teal-600 transition"
              disabled={loading || categoriesLoading}
            >
              {loading ? "Saving..." : "Save Checklist"}
            </button>
          )}
          {isViewMode && (
            <Link
              to="/hazardous-materials/checklists"
              className="px-6 py-2 rounded-full bg-[#00A8A8] text-white hover:bg-teal-600 transition"
            >
              Back To Checklists
            </Link>
          )}
        </div>
      </div>
    </>
  );
};

export default ChecklistConfigForm;
